import { Bell, ShieldAlert, Monitor, Gamepad2, Gift, Check, Clock } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["game_alerts"]) => void;
}

export function GameAlertsTab({ config, channels, onChange }: Props) {
  const cfg = config.game_alerts || {
    enabled: false,
    free_games_channel_id: "",
    updates_channel_id: "",
    platforms: ["epic", "steam"],
    games: ["cs2", "fortnite", "valorant", "gta5"]
  };

  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) => {
    onChange({ ...cfg, [k]: v });
  };

  const PLATFORMS = [
    { id: "epic", name: "Epic Games Store" },
    { id: "steam", name: "Steam Store" },
    { id: "gog", name: "GOG.com" },
    { id: "playstation", name: "PlayStation Store" },
    { id: "xbox", name: "Xbox Store" },
    { id: "itchio", name: "Itch.io" }
  ];

  const GAMES = [
    { id: "fortnite", name: "Fortnite" },
    { id: "cs2", name: "Counter-Strike 2" },
    { id: "valorant", name: "Valorant" },
    { id: "gta5", name: "Grand Theft Auto V" },
    { id: "lol", name: "League of Legends" }
  ];

  const togglePlatform = (id: string) => {
    const list = [...(cfg.platforms || [])];
    if (list.includes(id)) {
      update("platforms", list.filter((p) => p !== id));
    } else {
      update("platforms", [...list, id]);
    }
  };

  const toggleGame = (id: string) => {
    const list = [...(cfg.games || [])];
    if (list.includes(id)) {
      update("games", list.filter((g) => g !== id));
    } else {
      update("games", [...list, id]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Informative Hint Box */}
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          How Game Updates & Promos Works
        </p>
        <p>
          Automate free game announcements and popular game patch note alerts on your server! The bot polls public gaming feeds, 
          detects 100%-off game deals or developer releases, and broadcasts stunning **Section V2** alerts directly to your designated channels.
        </p>
      </div>

      {/* Main Settings Section */}
      <Section
        icon={Gamepad2}
        title="Game Updates & Promos"
        description="Toggle this module and configure target channels, platforms to check, and updates to track."
        enabled={cfg.enabled}
        onToggle={(v) => update("enabled", v)}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <Field
            label="Free Games Channel"
            hint="Where the bot posts giveaways and active 100%-off game promotions."
          >
            <ChannelPicker
              channels={channels}
              value={cfg.free_games_channel_id}
              onChange={(v) => update("free_games_channel_id", v === "__none__" ? "" : v)}
            />
          </Field>

          <Field
            label="Game Updates Channel"
            hint="Where the bot posts game-specific updates and developer patch notes."
          >
            <ChannelPicker
              channels={channels}
              value={cfg.updates_channel_id}
              onChange={(v) => update("updates_channel_id", v === "__none__" ? "" : v)}
            />
          </Field>
        </div>

        <div className="border-t border-border/50 my-6 pt-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Monitor className="h-4 w-4 text-primary" />
            Tracked Platforms (Free Games)
          </h4>
          <p className="text-xs text-muted-foreground mb-4">
            Select the platforms you want to monitor for active free game promotions and giveaways.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PLATFORMS.map((p) => {
              const active = (cfg.platforms || []).includes(p.id);
              return (
                <div
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-all ${active ? "border-primary/50 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}
                >
                  <span className="text-sm font-medium">{p.name}</span>
                  <div className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30"}`}>
                    {active && <Check className="h-3 w-3 stroke-[3]" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-border/50 my-6 pt-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Gamepad2 className="h-4 w-4 text-primary" />
            Tracked Game Updates
          </h4>
          <p className="text-xs text-muted-foreground mb-4">
            Select the popular multiplayer games you want to track for active developer patch notes and game update logs.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {GAMES.map((g) => {
              const active = (cfg.games || []).includes(g.id);
              return (
                <div
                  key={g.id}
                  onClick={() => toggleGame(g.id)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-all ${active ? "border-primary/50 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}
                >
                  <span className="text-sm font-medium">{g.name}</span>
                  <div className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30"}`}>
                    {active && <Check className="h-3 w-3 stroke-[3]" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Visual Section V2 Live Mockup Preview */}
      {cfg.enabled && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground tracking-wider uppercase flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            Live Preview (Section V2 Style)
          </h3>
          <Card className="p-4 bg-[#1e1f22] border-none text-white font-sans max-w-xl shadow-2xl relative overflow-hidden">
            {/* V2 Container Mockup */}
            <div className="space-y-3">
              {/* Header block (type: 9) */}
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-1">
                  <h2 className="text-lg font-bold flex items-center gap-2 text-white">
                    <Gift className="h-5 w-5 text-emerald-500" />
                    Free Game on Epic Games
                  </h2>
                  <p className="text-xs text-gray-400">Claim your free copy before next Thursday!</p>
                </div>
                {/* Accessory block (type: 11) */}
                <div className="h-12 w-12 rounded bg-cover bg-center shrink-0 border border-gray-700 shadow-md" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=200&auto=format&fit=crop')" }} />
              </div>

              {/* Divider spacer (type: 14) */}
              <div className="h-[1px] bg-gray-800 w-full" />

              {/* Details block (type: 10) */}
              <div className="text-sm text-gray-300 space-y-2 leading-relaxed">
                <p className="font-semibold text-white text-base">🎮 Death Stranding</p>
                <p className="text-xs text-gray-400">
                  From legendary creator Hideo Kojima comes an all-new, genre-defying experience. Sam Bridges must brave a world utterly transformed by the Death Stranding.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                  <div className="bg-gray-900/60 p-2 rounded border border-gray-800/40">
                    <span className="text-gray-500 block">Worth</span>
                    <span className="font-semibold text-emerald-400">~~$39.99~~ → FREE</span>
                  </div>
                  <div className="bg-gray-900/60 p-2 rounded border border-gray-800/40">
                    <span className="text-gray-500 block">Active Until</span>
                    <span className="font-semibold text-amber-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      In 5 days
                    </span>
                  </div>
                </div>
              </div>

              {/* Link button (type: 1 Action Row) */}
              <div className="pt-2 flex gap-2">
                <Button size="sm" className="bg-[#248046] hover:bg-[#1a6535] text-white font-semibold text-xs px-4 py-2 h-auto flex items-center gap-1 rounded">
                  <Gift className="h-3.5 w-3.5" />
                  Claim on Epic Games
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white hover:bg-gray-800 text-xs px-3 py-2 h-auto rounded border border-gray-800">
                  More Info
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
